import { useRef } from "react";
import { useInView } from "motion/react";

const categoryLogos: Record<string, { id: string; label: string; alt: string }[]> = {
  Languages: [
    { id: "javascript",  label: "JavaScript", alt: "JavaScript" },
    { id: "typescript",  label: "TypeScript", alt: "TypeScript" },
    { id: "swift",       label: "Swift", alt: "Swift" },
    { id: "php",         label: "PHP", alt: "PHP programming language" },
    { id: "html5",       label: "HTML5", alt: "HTML5 markup language" },
    { id: "css",         label: "CSS3", alt: "CSS 3 stylesheet language" },
  ],
  Frontend: [
    { id: "react",        label: "React", alt: "React" },
    { id: "nextdotjs",    label: "Next.js", alt: "Next.js" },
    { id: "tailwindcss",  label: "Tailwind CSS", alt: "Tailwind CSS" },
    { id: "framer",       label: "Framer Motion", alt: "Framer Motion" },
    { id: "vite",         label: "Vite", alt: "Vite" },
    { id: "radixui",      label: "Radix UI", alt: "Radix UI" },
  ],
  Backend: [
    { id: "nodedotjs",   label: "Node.js", alt: "Node.js" },
    { id: "laravel",     label: "Laravel", alt: "Laravel" },
    { id: "graphql",     label: "GraphQL", alt: "GraphQL" },
    { id: "express",     label: "Express", alt: "Express" },
    { id: "postgresql",  label: "PostgreSQL", alt: "PostgreSQL" },
    { id: "redis",       label: "Redis", alt: "Redis" },
  ],
  Mobile: [
    { id: "react",      label: "React Native", alt: "React Native" },
    { id: "swift",      label: "Swift", alt: "Swift" },
    { id: "capacitor",  label: "Capacitor", alt: "Capacitor" },
    { id: "expo",       label: "Expo", alt: "Expo" },
    { id: "apple",      label: "iOS", alt: "Apple iOS" },
    { id: "android",    label: "Android", alt: "Android" },
  ],
  "Cloud & Hosting": [
    { id: "amazonaws",     label: "AWS", alt: "Amazon Web Services" },
    { id: "googlecloud",   label: "Google Cloud", alt: "Google Cloud" },
    { id: "digitalocean",  label: "DigitalOcean", alt: "DigitalOcean" },
    { id: "vercel",        label: "Vercel", alt: "Vercel" },
    { id: "firebase",      label: "Firebase", alt: "Firebase" },
    { id: "linux",         label: "VPS", alt: "Virtual private server" },
  ],
  Security: [
    { id: "auth0",           label: "Auth0", alt: "Auth0" },
    { id: "jsonwebtokens",   label: "JWT", alt: "JSON Web Token" },
    { id: "letsencrypt",     label: "SSL", alt: "SSL certificate" },
    { id: "openid",          label: "OAuth2", alt: "OAuth 2.0" },
    { id: "cloudflare",      label: "Cloudflare", alt: "Cloudflare" },
    { id: "owasp",           label: "OWASP", alt: "OWASP security standards" },
  ],
  Tooling: [
    { id: "github",    label: "GitHub", alt: "GitHub" },
    { id: "figma",     label: "Figma", alt: "Figma" },
    { id: "jira",      label: "Jira", alt: "Jira" },
    { id: "jest",      label: "Jest", alt: "Jest" },
    { id: "cypress",   label: "Cypress", alt: "Cypress" },
    { id: "postman",   label: "Postman", alt: "Postman" },
    { id: "clickup",   label: "ClickUp", alt: "ClickUp" },
    { id: "storybook", label: "Storybook", alt: "Storybook" },
    { id: "docker",    label: "Docker", alt: "Docker" },
  ],
};

interface Props {
  category: string;
  items?: string[];
}

const TechCategoryBanner = ({ category }: Props) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const isBannerInView = useInView(bannerRef, { once: true, margin: "-50px 0px -50px 0px" });

  const logos = categoryLogos[category] ?? categoryLogos["Languages"];
  const gridLogos = logos.slice(0, 9);

  return (
    <div
      ref={bannerRef}
      className="w-full h-full relative overflow-hidden z-0"
    >
      {/* Flyingcode logo wallpaper */}
      <div className="absolute inset-0 -z-20 grid grid-cols-7 md:grid-cols-4 gap-[8px] md:gap-[12px] p-0 opacity-[0.03] pointer-events-none overflow-hidden">
        {Array.from({ length: 25 }).map((_, i) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={i}
            src="/assets/brand/logo.svg"
            alt="Flyingcode logo"
            aria-hidden="true"
            width={83}
            height={53}
            loading="lazy"
            className="w-full h-auto object-contain"
          />
        ))}
      </div>

      <div className="absolute z-[1] top-[50%] -translate-y-2/4 start-2/4 -translate-x-2/4 w-[70%] md:w-full grid grid-cols-3 gap-[6px] md:gap-[10px] scale-100 md:scale-125 -rotate-15">
        {gridLogos.map((logo, index) => (
          <div
            key={index}
            className={`grid__item ${index % 3 === 1 ? "-mt-10" : ""}`}
          >
            <div
              className={`w-full aspect-square rounded-xl md:rounded-2xl p-[14px] md:p-[25px] flex items-center justify-center animate-icon-drift ${
                index === 4 ? "bg-[#160A2E]" : "bg-[#12102a]"
              }`}
              style={{ animationDelay: `${index * 0.2}s`, animationDuration: `${3 + index * 0.2}s` }}
            >
              <svg
                role="img"
                aria-label={logo.alt}
                className="w-full h-full text-white"
                viewBox="0 0 24 24"
              >
                <use href={`/assets/icons/tech-sprite.svg#icon-${logo.id}`} />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* OVERLAY */}
      <div className="absolute inset-0 z-[2] bg-[linear-gradient(180deg,_#3C1C63_0%,_rgba(60,28,99,0)_99.87%)] mix-blend-plus-lighter" />
    </div>
  );
};

export default TechCategoryBanner;
